/**
 * Makes every axios request carry the signed token.
 *
 * WHY THIS IS A GLOBAL PATCH RATHER THAN 20 EDITS
 *
 * This app creates axios clients in twenty places - the shared ones in
 * services/ and plugins/, plus a private client inside most HR modules - and a
 * further thirty services use the bare `axios` default. A request that does not
 * carry the token arrives unauthenticated, and the server then runs it
 * UNRESTRICTED: the legacy procedures have no tenant filter, so the designation
 * list came back with all four tenants' rows in its total.
 *
 * Adding the header by hand in twenty files would work until the twenty-first
 * client was written, and the failure would be silent - a screen quietly showing
 * another customer's data. So instead this wraps axios.create, so every client
 * built from here on gets the interceptor, and registers one on the default
 * instance for the services that use it directly.
 *
 * IMPORT THIS FIRST IN main.ts. Module imports are evaluated in order, and a
 * client created at module scope before the patch runs would not be wrapped.
 *
 * This is transport, not security: the server verifies the token's signature and
 * ignores anything it cannot verify. Sending it from one place only means the
 * server is told who is asking.
 */

import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

export const ACCESS_TOKEN_KEY = 'saas_access_token'

function attachToken(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  try {
    // Read per request rather than once: the token changes on sign-in and is
    // cleared on sign-out, and a cached copy would outlive both.
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)

    // Never overwrite an Authorization header a caller set deliberately.
    if (accessToken && config.headers && !config.headers['Authorization']) {
      config.headers['Authorization'] = `Bearer ${accessToken}`
    }
  } catch {
    // localStorage can throw in a private window. A request without the token
    // still works; it is simply unscoped, exactly as before this existed.
  }

  return config
}

/** Adds the interceptor to one client, guarding against doing it twice. */
export function attachTenantAuth(instance: AxiosInstance): AxiosInstance {
  const marked = instance as AxiosInstance & { __tenantAuthAttached?: boolean }
  if (marked.__tenantAuthAttached) return instance

  instance.interceptors.request.use(attachToken, (error) => Promise.reject(error))
  marked.__tenantAuthAttached = true

  return instance
}

/**
 * Installs the patch. Call once, as early as possible.
 */
export function installTenantAuth(): void {
  // The default instance, used by the ~30 services that import `axios` directly.
  attachTenantAuth(axios as unknown as AxiosInstance)

  // Every instance created from now on.
  const originalCreate = axios.create.bind(axios)
  axios.create = ((config?: Parameters<typeof originalCreate>[0]) =>
    attachTenantAuth(originalCreate(config))) as typeof axios.create
}

export default installTenantAuth
